<div class="mba ov">
    <!-- Filters -->
    <div class="ov-filters">
        <div class="ov-seg" role="group" aria-label="Format">
            @for (format of formats; track format.id) {
            <button type="button" [class.on]="scan().query.format === format.id"
                [attr.aria-pressed]="scan().query.format === format.id" (click)="selectFormat(format.id)">{{ format.label }}</button>
            }
        </div>
        <span class="ov-sp"></span>
        @if (!loading()) {
        <span class="ov-sub ov-upd">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
            </svg>
            {{ scan().statsUpdatedLabel ?? 'We check for new posts every few hours' }}
        </span>
        }
        <select class="ov-sel" aria-label="Period" (change)="onPeriod($event)">
            @for (period of periods; track period.id) {
            <option [value]="period.id" [selected]="scan().query.period === period.id">{{ period.label }}</option>
            }
        </select>
    </div>

    <!-- Hero: chart + at a glance -->
    <section class="ov-hero" aria-label="Campaign trend">
        <div class="ov-card ov-chartcard">
            <div class="ov-mtabs" role="group" aria-label="Chart metric">
                @for (tab of scan().tabs; track tab.id) {
                <button type="button" class="ov-mtab" [class.on]="scan().query.metric === tab.id"
                    [attr.aria-pressed]="scan().query.metric === tab.id" (click)="selectMetric(tab.id)">
                    <span class="ov-k">{{ tab.label }}</span>
                    @if (loading()) {
                    <span class="ov-skel ov-skel-value"></span>
                    <span class="ov-skel ov-skel-line"></span>
                    } @else {
                    <span class="v">{{ tab.valueText }}</span>
                    @switch (tab.delta.kind) {
                    @case ('change') {
                    @if (tab.delta.kind === 'change') {
                    <span class="ov-delta" [class.dn]="!tab.delta.up">
                        @if (tab.delta.up) {
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                            <polyline points="16 7 22 7 22 13" />
                        </svg>
                        <span class="sr-only">Up</span>
                        } @else {
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                            <polyline points="16 17 22 17 22 11" />
                        </svg>
                        <span class="sr-only">Down</span>
                        }
                        {{ tab.delta.percent }}%
                        <span class="ov-sub">vs previous {{ tab.delta.days }} days</span>
                    </span>
                    }
                    }
                    @case ('since') {
                    @if (tab.delta.kind === 'since') {
                    <span class="ov-sub">Since {{ tab.delta.label }}</span>
                    }
                    }
                    @case ('no-earlier') {
                    <span class="ov-sub">No earlier data</span>
                    }
                    @default {
                    <span class="ov-sub">No data yet</span>
                    }
                    }
                    }
                </button>
                }
            </div>

            <div class="ov-chart">
                @if (loading()) {
                <div class="ov-skel ov-skel-chart" role="status" aria-label="Loading the chart"></div>
                } @else {
                <div echarts [options]="chartOptions()" [autoResize]="true" class="ov-echart" role="img"
                    [attr.aria-label]="scan().metricLabel + ' per day, ' + scan().periodLabel.toLowerCase()"></div>
                @if (scan().status === 'empty') {
                <div class="ov-cempty">
                    <div>
                        <span class="ic" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" />
                                <path d="M4 6h.01" />
                                <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
                                <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67" />
                                <path d="M12 18h.01" />
                                <path d="M17.99 11.66A6 6 0 0 1 15.77 16.67" />
                                <circle cx="12" cy="12" r="2" />
                                <path d="m13.41 10.59 5.66-5.66" />
                            </svg>
                        </span>
                        @if (awaitingStats()) {
                        <b>Fetching stats</b>
                        <span class="ov-sub">We've found {{ postsFound() }} {{ postsFound() === 1 ? 'post' : 'posts' }} for this brief. The trend line starts once their stats come in.</span>
                        } @else {
                        <b>Waiting for the first post</b>
                        <span class="ov-sub">Your trend line starts when we find the first post that matches this brief.</span>
                        }
                    </div>
                </div>
                }
                }
            </div>

            <div class="ov-cf">
                @if (scan().status === 'live' && scan().query.metric !== 'posts') {
                <span class="ov-sub lg"><i></i>A dot marks the day a post went live, in the creator's colour. Each post's stats count on that day.</span>
                } @else {
                <span></span>
                }
                <span class="ov-sub">Daily · {{ scan().periodLabel }}</span>
            </div>
        </div>

        <div class="ov-card ov-glance">
            <div class="h">
                <h2 class="ov-k">At a glance</h2>
                <span class="ov-sub">{{ scan().periodLabel }}</span>
            </div>
            <div class="ov-cp">
                <span class="ov-k">Creators posted</span>
                @if (loading()) {
                <span class="ov-skel ov-skel-value"></span>
                } @else {
                <span class="v">{{ scan().glance.posted }}<small> / {{ scan().glance.hired }}</small></span>
                <div class="ov-bar" role="progressbar" aria-label="Creators posted" [attr.aria-valuenow]="scan().glance.posted"
                    aria-valuemin="0" [attr.aria-valuemax]="scan().glance.hired">
                    <i [style.width.%]="scan().glance.progress"></i>
                </div>
                <span class="ov-sub">
                    @if (scan().glance.hired === 0) {
                    No creators on this brief yet
                    } @else if (scan().glance.notPosted === 0) {
                    Every creator has posted
                    } @else if (scan().glance.notPosted === 1) {
                    1 creator hasn't posted yet
                    } @else {
                    {{ scan().glance.notPosted }} creators haven't posted yet
                    }
                </span>
                }
            </div>
            <dl class="ov-glist">
                <div class="ov-gr">
                    <dt>Avg. engagement rate</dt>
                    <dd>{{ scan().glance.averageEngagementRate.toFixed(2) }}%</dd>
                </div>
                <div class="ov-gr">
                    <dt>Likes</dt>
                    <dd>{{ compact(scan().glance.likes) }}</dd>
                </div>
                <div class="ov-gr">
                    <dt>Comments</dt>
                    <dd>{{ compact(scan().glance.comments) }}</dd>
                </div>
                <div class="ov-gr">
                    <dt>Shares + saves</dt>
                    <dd>{{ compact(scan().glance.sharesAndSaves) }}</dd>
                </div>
                <div class="ov-gr">
                    <dt>Story views</dt>
                    <dd>{{ compact(scan().glance.storyViews) }}</dd>
                </div>
            </dl>
        </div>
    </section>

    <!-- Creator leaderboard -->
    <section class="ov-col" aria-labelledby="ov-lb-title">
        <div class="ov-sh">
            <div>
                <h2 id="ov-lb-title" class="ov-h">Creator Leaderboard</h2>
                <p class="ov-sub">Ranked by {{ scan().metricLabel.toLowerCase() }} for the selected period. Change the metric in the chart above.</p>
            </div>
        </div>
        <div class="ov-card ov-lb">
            @if (loading()) {
            @for (row of skeletonRows; track row) {
            <div class="ov-lr" aria-hidden="true">
                <span class="ov-rk">–</span>
                <span class="ov-skel ov-skel-line"></span>
                <span class="ov-skel ov-skel-line"></span>
                <span></span>
                <span></span>
            </div>
            }
            } @else {
            <ol class="ov-list">
                @for (row of scan().leaders; track row.creatorId; let index = $index) {
                <li class="ov-lr">
                    <span class="ov-rk">{{ index + 1 }}</span>
                    <span class="ov-who">
                        @if (row.photoUrl) {
                        <img class="ov-av" [src]="row.photoUrl" alt="" />
                        } @else {
                        <span class="ov-av" aria-hidden="true" [style.background]="row.color">{{ row.initial }}</span>
                        }
                        <span>
                            <b>{{ row.name }}</b>
                            <span class="ov-sub">{{ row.handle }}@if (row.handle) { · }{{ compact(row.followers) }} followers</span>
                        </span>
                    </span>
                    <span class="ov-track" aria-hidden="true"><i [style.width.%]="row.share"></i></span>
                    <span class="ov-val">{{ row.valueText }}</span>
                    <span class="ov-sub ov-pn">{{ row.posts }} {{ row.posts === 1 ? 'post' : 'posts' }}</span>
                </li>
                }
            </ol>
            @if (scan().waiting.length) {
            <div class="ov-waith">
                <h3 class="ov-k">Not posted yet · {{ scan().waiting.length }}</h3>
                <span class="ov-sub">No matching posts found on their accounts</span>
            </div>
            <ul class="ov-list">
                @for (row of scan().waiting; track row.creatorId) {
                <li class="ov-lr wait">
                    <span class="ov-rk" aria-hidden="true">–</span>
                    <span class="ov-who">
                        <span class="ov-av" aria-hidden="true">{{ row.initial }}</span>
                        <span>
                            <b>{{ row.name }}</b>
                            <span class="ov-sub">{{ row.handle }}@if (row.handle) { · }{{ compact(row.followers) }} followers</span>
                        </span>
                    </span>
                    <span class="ov-track" aria-hidden="true"></span>
                    <button type="button" class="ov-btn" aria-disabled="true" (click)="remind(row)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M10.268 21a2 2 0 0 0 3.464 0" />
                            <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
                        </svg>
                        Send reminder
                    </button>
                </li>
                }
            </ul>
            }
            @if (!scan().leaders.length && !scan().waiting.length) {
            <p class="ov-sub ov-none">No creators on this brief yet. Add creators to start monitoring their accounts.</p>
            }
            }
        </div>
    </section>

    <!-- Top performing content -->
    <section class="ov-col" aria-labelledby="ov-top-title">
        <div class="ov-sh">
            <div>
                <h2 id="ov-top-title" class="ov-h">Top Performing Content</h2>
                <p class="ov-sub">Best posts on this brief by {{ scan().topMetricLabel.toLowerCase() }}, all time.</p>
            </div>
            @if (scan().top.length) {
            <button type="button" class="ov-btn ghost" (click)="showResults.emit()">
                See all results
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                </svg>
            </button>
            }
        </div>
        @if (loading()) {
        <div class="ov-cgrid" aria-hidden="true">
            @for (row of skeletonRows; track row) {
            <div class="ov-skel ov-skel-card"></div>
            }
        </div>
        } @else if (scan().top.length) {
        <div class="ov-cgrid">
            @for (item of scan().top; track item.post.socialPostId) {
            <button type="button" class="ov-pc" (click)="openPost(item.post.socialPostId)"
                [attr.aria-label]="'Open ' + (item.post.creatorName || item.post.handle) + '\'s ' + item.formatLabel">
                <span class="ov-th" [style.--creator]="item.color">
                    @if (item.post.coverUrl) {
                    <img [src]="item.post.coverUrl" alt="" loading="lazy" />
                    }
                    <span class="fm">
                        <span class="ov-logo" [class.tt]="item.format === 'tiktok'" aria-hidden="true">
                            @if (item.format === 'tiktok') {
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.79a5.68 5.68 0 1 0 4.91 5.61V9.42a7.35 7.35 0 0 0 4.3 1.38V7.71a4.28 4.28 0 0 1-3.24-1.89Z" /></svg>
                            } @else {
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
                            }
                        </span>
                        {{ item.formatLabel }}
                    </span>
                    <span class="rk">{{ item.rank }}</span>
                    <span class="play" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5v15l12.5-7.5L7 4.5Z" /></svg>
                    </span>
                </span>
                <span class="ov-pm">
                    <b>{{ item.post.creatorName || item.post.handle }}</b>
                    <span class="ov-sub">{{ item.post.postedLabel }}@if (item.post.postedLabel) { · }{{ compact(item.post.followers) }} followers</span>
                    <span class="ov-pst">
                        <span><strong>{{ compact(item.post.views) }}</strong> views</span>
                        <span><strong>{{ compact(item.post.engagement) }}</strong> eng.</span>
                        <span><strong>{{ item.post.engagementRate.toFixed(1) }}%</strong> ER</span>
                    </span>
                </span>
            </button>
            }
        </div>
        } @else {
        <div class="ov-card ov-emptybox">
            <span class="ic" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" />
                    <path d="M4 6h.01" />
                    <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
                    <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67" />
                    <path d="M12 18h.01" />
                    <path d="M17.99 11.66A6 6 0 0 1 15.77 16.67" />
                    <circle cx="12" cy="12" r="2" />
                    <path d="m13.41 10.59 5.66-5.66" />
                </svg>
            </span>
            <b>{{ awaitingStats() ? 'No stats yet' : scan().status === 'empty' ? 'No posts found yet' : 'No posts in this format' }}</b>
            <span class="ov-sub">
                @if (awaitingStats()) {
                Posts appear here with their views and engagement once we've fetched their stats.
                } @else if (scan().status === 'empty') {
                We check your creators' Instagram and TikTok accounts for posts that match this brief every few hours. Matching posts appear here with their stats.
                } @else {
                None of the posts found so far are in this format. Try All formats.
                }
            </span>
        </div>
        }
    </section>
</div>

<mbtm-mon-post-drawer [posts]="scan().drawerList" [(openedPostId)]="openedPostId" />
